import { z } from 'zod';
import { PromptExperience, PromptExperienceScope } from './ai-prompt.js';
/**
 * Propose–Probe–Compose cho prompt ghi đè — APM-H-aq (mượn `recursion.py` /
 * `proposer.py` của Harness2, `plan-harness2-adoption.md` §5).
 *
 * Skill `harness.propose_prompt` chạy **ba** lượt cho một skill đích: hai lượt
 * `propose` (một theo phạm vi `invariant`, một theo `domain`) và một lượt
 * `compose` gộp hai ứng viên dựa trên kết quả đã chạy thử. Kết cục của mỗi lượt
 * là một bản nháp prompt kèm bản ghi bằng chứng — người kích hoạt (luật #6 áp
 * cho chính harness).
 */
export const PROPOSE_PROMPT_SKILL = 'harness.propose_prompt';
/** Nhãn ẩn danh ở bước gộp — xáo theo seed, model không biết bản nào là gốc. */
export const ProposeCandidateLabel = z.enum(['A', 'B', 'C']);
/** Mã ca `C1`, `C2`… — model chỉ thấy mã, không thấy tên ca hay khẳng định (APM-H-an). */
export const ProposeCaseCode = z.string().regex(/^C\d+$/);
const RunIds = z.array(z.uuid()).min(1).max(20);
const TargetSkill = z.string().trim().min(1).max(60);
/** Lượt `propose`: một phạm vi, các run hỏng làm bằng chứng. */
export const ProposePromptParams = z.object({
    mode: z.literal('propose'),
    targetSkill: TargetSkill,
    scope: PromptExperienceScope,
    runIds: RunIds,
});
/**
 * Lượt `compose`: ba bản prompt ẩn danh (gốc + hai ứng viên, nhãn đã xáo), diff
 * giữa chúng, và kết cục **đạt/không đạt** theo mã ca — không điểm, không tên bản.
 */
export const ComposePromptParams = z.object({
    mode: z.literal('compose'),
    targetSkill: TargetSkill,
    runIds: RunIds,
    candidates: z.array(z.object({ label: ProposeCandidateLabel, promptBody: z.string() })).length(3),
    /** Diff từ bản đứng đầu (theo nhãn) sang hai bản còn lại — không lộ bản nào là gốc. */
    diffs: z.array(z.object({ from: ProposeCandidateLabel, to: ProposeCandidateLabel, diff: z.string() })),
    cases: z.array(z.object({
        code: ProposeCaseCode,
        /** Pass^k của từng bản trên ca này. */
        passed: z.record(ProposeCandidateLabel, z.boolean()),
    })),
});
export const ProposeSkillParams = z.discriminatedUnion('mode', [
    ProposePromptParams,
    ComposePromptParams,
]);
/** Output của skill: bản prompt đầy đủ (không phải chuỗi patch) + bản ghi bằng chứng. */
export const ProposedPromptDraft = z.object({
    promptBody: z.string().min(20).max(50_000),
    experience: PromptExperience,
});
//# sourceMappingURL=ai-prompt-propose.js.map